import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useEffect, useMemo, useRef, useState } from 'react';
import { getBscGlobalState, sendScreenEvent } from '../../../common/utils';
import { isSSR } from '../../../common/utils/is-ssr';
import { ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY, ATLASSIAN_SHOW_BANNER_COOKIE_KEY } from '../../../constants';
import { setStrictlyNecessaryCookie } from '../../../controllers/cookie-controls';
import { getCookie } from '../../../controllers/cookie-controls/get-cookie';
import { shouldShowBannerInSSRContext } from '../utils';
export var calculateBannerStateViaBit = function calculateBannerStateViaBit(isInitialRender, initialPreferences) {
  // We don't have a great way to handle dealing with consent timestamps right here, as we have no global state/store, so we have to precompute
  // banner visibility state when we fetch consents, and store it in a cookie to be read later, here.
  var shouldShowBannerBit = getCookie(ATLASSIAN_SHOW_BANNER_COOKIE_KEY);
  var inFlowWhereBannerStateIsUnknown = initialPreferences && isInitialRender && shouldShowBannerBit === undefined;
  if (isSSR() || inFlowWhereBannerStateIsUnknown) {
    // When coming from SSR, this hook will only execute once and won't update later
    // Instead, if we're on the server, have initial preference data with valid consents, don't render
    // On later checks (rehydration), this will always fail and skip to the normal rendering logic

    // Once we're rehydrating, if have been provided initial prefs (from SSR), on the "first render", the banner cookie may still be missing.
    // Due to React lifecycle where this useMemo is executing prior to the useEffect in the usePreferences hook, which sets the core cookies.
    // We should give this flow the same treatment as rendering in an SSR context to match the behavior of the SSR flow to reduce any potential UX flicker.
    return shouldShowBannerInSSRContext(initialPreferences);
  }
  var shouldShowBanner = shouldShowBannerBit ? !!Number(shouldShowBannerBit) : false;
  return shouldShowBanner;
};

/**
 * This hook is responsible for determining whether or not the consent banner should be shown based on the user's
 * preferences and the current state of the banner cookie, as well as if the we're rendering from an SSR context.
 *
 * @param preferences - The current state of the user's preferences
 * @param initialPreferences - Any initially provided preferences from consuming applications (used for SSR)
 * @param isInitialRender - A boolean to track if this is the "first" render of the component
 * @param isPrefetchedPreferencesState - A boolean that indicates if are fetching consents internally to our component
 * @returns showBanner - Whether or not the banner should be shown
 * @returns setIsDismissed - Setter function to update the dismissed state of the banner
 */
var useBannerVisibility = function useBannerVisibility(_ref) {
  var preferences = _ref.preferences,
    initialPreferences = _ref.initialPreferences,
    isInitialRender = _ref.isInitialRender,
    isPrefetchedPreferencesState = _ref.isPrefetchedPreferencesState;
  var isEventSent = useRef(false);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDismissed = _useState2[0],
    setIsDismissedProp = _useState2[1];
  var setIsDismissed = function setIsDismissed(isDismissed) {
    if (isDismissed) {
      setStrictlyNecessaryCookie(ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY, Date.now().toString(), {
        path: '/'
      });
    }
    setIsDismissedProp(isDismissed);
  };
  var showBanner = useMemo(function () {
    return getShowBanner({
      preferences: preferences,
      initialPreferences: initialPreferences,
      isInitialRender: isInitialRender,
      isPrefetchedPreferencesState: isPrefetchedPreferencesState,
      isDismissed: isDismissed
    });
  }, [isInitialRender, initialPreferences, isPrefetchedPreferencesState, preferences, isDismissed]);
  useEffect(function () {
    if (!isEventSent.current && showBanner) {
      sendScreenEvent({
        name: 'cookieConsentBanner'
      });
      isEventSent.current = true;
    }
  }, [isEventSent, showBanner]);
  return {
    showBanner: showBanner,
    setIsDismissed: setIsDismissed
  };
};
export var getShowBanner = function getShowBanner(_ref2) {
  var preferences = _ref2.preferences,
    initialPreferences = _ref2.initialPreferences,
    isInitialRender = _ref2.isInitialRender,
    isPrefetchedPreferencesState = _ref2.isPrefetchedPreferencesState,
    isDismissed = _ref2.isDismissed;
  if (getBscGlobalState().localConsentMode) {
    // In V1, we only checked based on the existence of the preferences
    var prefsMissing = !preferences;
    if (isSSR()) {
      // This logic will execute normally in V2, but for now, as SSR prefetching has not been implemented completely
      // just hide the banner if we're missing the data (always will be in SSR-context) and let the UI handle it on rehydration
      return !prefsMissing && !isDismissed;
    }
    return prefsMissing && !isDismissed;
  }
  var shouldShowBanner = calculateBannerStateViaBit(isInitialRender, initialPreferences);
  var unknownPreferences = !isPrefetchedPreferencesState && !preferences;
  return (unknownPreferences || shouldShowBanner) && !isDismissed;
};
export default useBannerVisibility;